import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, ExternalLink, Info, Mail, Paperclip, Send, ShieldAlert } from "lucide-react";
import {
  CHANNEL_CONFIDENCE,
  CHANNEL_STATUSES,
  CHANNEL_TYPES,
  resolveInstitutionChannel,
} from "@/data/institutionChannels";
import {
  copyTextSafely,
  createComposeTarget,
  createEmailSubject,
  getPreferredEmailProvider,
  isSameEmailAddress,
  isValidEmailAddress,
  NEW_WINDOW_RESULT,
  openSafeNewWindow,
} from "@/lib/applicationDelivery";
import { BILLING_PROVIDER_OPTIONS } from "@/data/digitalSubscriptions";
import { DELIVERY_CHANNEL_KINDS } from "@/lib/deliveryState";

const COPY_ERROR = "Kopyalama yapılamadı. Metni seçerek kopyalayabilirsiniz.";
const DISCOVERY_API = `${process.env.REACT_APP_BACKEND_URL}/api/institution-channels/discover`;
const PROVIDER_LABELS = {
  gmail: "Gmail’de aç",
  outlook: "Outlook’ta aç",
  mailto: "E-posta uygulamasında aç",
};

export function createEmailChannelOpenedDescriptor(provider, reopen) {
  return {
    label: PROVIDER_LABELS[provider],
    kind: DELIVERY_CHANNEL_KINDS.EMAIL,
    channelType: "email",
    reopen,
  };
}

function showPopupError() {
  toast.error("Yeni pencere açılamadı. Tarayıcınızın açılır pencere ayarını kontrol edin.", {
    id: "application-popup-error",
  });
}

function showPopupUnverified(message) {
  toast.info(message, { id: "application-popup-unverified" });
}

export default function OfficialChannelsCard({
  issueType,
  company,
  companyId,
  serviceScope,
  billingProvider,
  commerceProblem,
  purchaseAccountMode,
  generatedText = "",
  onGeneratedTextChange,
  hasNationalId = false,
  subscriptionNumber = "",
  attachmentCount = 0,
  attachments = [],
  senderEmail = "",
  onChannelOpened,
  enableLiveDiscovery = true,
  flow,
  transactionNumber = "",
}) {
  const [manualEmailOpen, setManualEmailOpen] = useState(false);
  const [manualRecipient, setManualRecipient] = useState("");
  const [liveDiscovery, setLiveDiscovery] = useState(null);
  const [discoveryLoading, setDiscoveryLoading] = useState(false);
  const [discoveryError, setDiscoveryError] = useState(false);
  const resolution = useMemo(
    () => resolveInstitutionChannel(companyId || company, issueType, {
      serviceScope,
      billingProvider,
      commerceProblem,
      purchaseAccountMode,
    }),
    [company, companyId, issueType, serviceScope, billingProvider, commerceProblem, purchaseAccountMode]
  );
  const institution = resolution.institution;
  const isDigitalSubscription = resolution.requestedInstitution?.category === "digital"
    || institution?.category === "digital";
  const isEcommerce = resolution.requestedInstitution?.category === "ecommerce"
    || institution?.category === "ecommerce";
  const billingProviderLabel = BILLING_PROVIDER_OPTIONS
    .find((option) => option.value === billingProvider)?.label;
  const verifiedRecipient = resolution.primaryChannel?.type === CHANNEL_TYPES.EMAIL
    ? resolution.primaryChannel.value
    : null;
  const liveCandidate = liveDiscovery?.recommendedCandidate?.confidence === "high"
    && liveDiscovery.recommendedCandidate.candidateStatus === "strong_candidate"
    ? liveDiscovery.recommendedCandidate
    : null;
  const liveRecipient = liveCandidate?.type === "direct_cancellation_email"
    ? liveCandidate.value
    : null;
  const trustedRecipient = verifiedRecipient || liveRecipient;
  const registryOfficialChannel = resolution.primaryChannel
    && resolution.primaryChannel.type !== CHANNEL_TYPES.EMAIL
    ? resolution.primaryChannel
    : null;
  const liveOfficialChannel = liveCandidate
    && ["official_form", "online_account", "official_contact_page"].includes(liveCandidate.type)
    ? liveCandidate
    : null;
  const primaryOfficialChannel = registryOfficialChannel || liveOfficialChannel;
  const registryAlternativeChannels = resolution.alternativeChannels.filter((channel) => (
    [CHANNEL_TYPES.OFFICIAL_FORM, CHANNEL_TYPES.ONLINE_ACCOUNT, CHANNEL_TYPES.OFFICIAL_CONTACT_PAGE].includes(channel.type)
  ));
  const supportingInfoChannels = resolution.alternativeChannels.filter((channel) => (
    channel.showOnResult
    && [CHANNEL_TYPES.PHONE, CHANNEL_TYPES.POSTAL].includes(channel.type)
  ));
  const visibleEscalationChannels = resolution.escalationChannels
    .filter((channel) => channel.showOnResult);
  const channelRequirements = (resolution.primaryChannel?.requirements || [])
    .filter((requirement) => requirement.appliesTo.includes(issueType));
  const isLivePrimary = Boolean(!resolution.primaryChannel && liveCandidate);
  const manualRecipientHasValue = manualRecipient.trim().length > 0;
  const manualRecipientFormatValid = isValidEmailAddress(manualRecipient);
  const manualRecipientMatchesSender = manualRecipientFormatValid
    && isSameEmailAddress(manualRecipient, senderEmail);
  const manualRecipientValid = manualRecipientFormatValid && !manualRecipientMatchesSender;
  const recipient = trustedRecipient || (manualRecipientValid ? manualRecipient.trim() : "");
  const displayedRecipient = trustedRecipient
    || (manualRecipientFormatValid ? manualRecipient.trim() : primaryOfficialChannel?.label || "Doğrulanmış doğrudan kanal yok");
  const preferredProvider = getPreferredEmailProvider(senderEmail);
  const emailSubject = createEmailSubject(
    issueType,
    institution?.companyName || company,
    subscriptionNumber,
    { flow, transactionNumber, commerceProblem }
  );

  useEffect(() => {
    setLiveDiscovery(null);
    setDiscoveryError(false);
    if (!enableLiveDiscovery || !institution || isDigitalSubscription || isEcommerce || resolution.primaryChannel || !["iptal", "fatura", "iade", "hakem"].includes(issueType)) {
      setDiscoveryLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setDiscoveryLoading(true);
    axios.post(DISCOVERY_API, {
      company_id: institution.companyId,
      issue_type: issueType,
    }, { signal: controller.signal, timeout: 17000 })
      .then(({ data }) => setLiveDiscovery(data))
      .catch((error) => {
        if (!axios.isCancel(error) && error?.name !== "CanceledError") setDiscoveryError(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setDiscoveryLoading(false);
      });
    return () => controller.abort();
  }, [enableLiveDiscovery, institution, isDigitalSubscription, isEcommerce, issueType, resolution.primaryChannel]);

  const copyValue = async (value, kind) => {
    const copied = await copyTextSafely(value);
    if (!copied) {
      toast.error(COPY_ERROR, { id: `application-copy-${kind}` });
      return false;
    }
    const message = kind === "subject"
      ? "E-posta konusu kopyalandı."
      : kind === "recipient"
        ? "E-posta adresi kopyalandı."
        : kind === "all"
          ? "Başvurunun tamamı kopyalandı."
          : "Başvuru metni kopyalandı.";
    toast.success(message, { id: `application-copy-${kind}` });
    return true;
  };

  const announceChannelOpened = (label, reopen, channelType) => {
    onChannelOpened?.({
      label,
      reopen,
      kind: DELIVERY_CHANNEL_KINDS.OFFICIAL_PAGE,
      channelType,
    });
  };

  const openCompose = async (provider, announce = true) => {
    const target = createComposeTarget({
      provider,
      recipient,
      subject: emailSubject,
      body: generatedText,
      hasNationalId,
    });
    if (!target) {
      toast.error("E-posta bağlantısı oluşturulamadı. Alıcı adresini kontrol edip tekrar deneyin.", {
        id: "application-compose-url-error",
      });
      return false;
    }

    if (target.shouldCopyBody) {
      const copied = await copyTextSafely(generatedText);
      if (!copied) {
        toast.error(COPY_ERROR, { id: "application-compose-copy" });
        return false;
      }
    }

    if (provider === "mailto") {
      try {
        window.location.assign(target.url);
      } catch {
        toast.error("E-posta uygulaması açılamadı. Metni kopyalayarak devam edebilirsiniz.", {
          id: "application-mailto-error",
        });
        return false;
      }
    } else {
      const openResult = openSafeNewWindow(target.url);
      if (openResult === NEW_WINDOW_RESULT.FAILED) {
        showPopupError();
        return false;
      }
      if (openResult === NEW_WINDOW_RESULT.UNVERIFIED) {
        showPopupUnverified("E-posta ekranı açılmadıysa tekrar deneyin veya tarayıcınızın açılır pencere iznini kontrol edin.");
      }
    }

    if (target.shouldCopyBody) {
      toast.success("Başvuru metni kopyalandı. Açılan e-postanın mesaj alanına yapıştırın.", {
        id: "application-compose-copy",
      });
    }
    if (announce) {
      onChannelOpened?.(createEmailChannelOpenedDescriptor(
        provider,
        () => openCompose(provider, false)
      ));
    }
    return true;
  };

  const copyThenOpenOfficialChannel = async (channel, announce = true) => {
    if (!isDigitalSubscription) {
      const copied = await copyTextSafely(generatedText);
      if (!copied) {
        toast.error(COPY_ERROR, { id: "application-official-copy" });
        return false;
      }
    }
    const openResult = openSafeNewWindow(channel.value);
    if (openResult === NEW_WINDOW_RESULT.FAILED) {
      showPopupError();
      return false;
    }
    if (openResult === NEW_WINDOW_RESULT.UNVERIFIED) {
      showPopupUnverified("Resmî sayfa açılmadıysa tekrar deneyin veya tarayıcınızın açılır pencere iznini kontrol edin.");
    }
    toast.success(isDigitalSubscription || isEcommerce
      ? "Resmî işlem sayfası açıldı. Oturum açma ve onay adımlarını siz tamamlayın."
      : "Başvuru metniniz kopyalandı. Açılan sayfadaki açıklama alanına yapıştırabilirsiniz.", {
      id: "application-official-copy",
    });
    if (announce) {
      announceChannelOpened(
        channel.label,
        () => copyThenOpenOfficialChannel(channel, false),
        channel.type
      );
    }
    return true;
  };

  const agentMessage = liveDiscovery?.agentMessage
    || (discoveryError
      ? "Kurumun resmî sitesindeki güncel kanal şu anda kontrol edilemedi. Doğrulanmamış bir adres önermiyorum."
      : resolution.agentMessage);
  const source = resolution.source || (liveCandidate ? {
    url: liveCandidate.sourceUrl,
    title: liveCandidate.sourceTitle,
    officialDomain: liveCandidate.officialDomain,
    verifiedAt: liveCandidate.discoveredAt,
    confidence: liveCandidate.confidence,
  } : null);
  const fallbackChannel = !liveCandidate ? liveDiscovery?.fallbackChannel : null;

  return (
    <section data-testid="official-channels-card" className="mt-5 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm md:p-6">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#14A89F]">
        <Send size={14} /> Abonelik İptal Asistanı
      </div>
      <p className="mt-2 text-sm font-semibold leading-relaxed text-[#0F3D4A]">
        {isDigitalSubscription
          ? "Faturalandırma sağlayıcınıza uygun resmî işlem kanalını belirledim."
          : isEcommerce
            ? "Siparişiniz için platformun doğrulanmış resmî işlem kanalını belirledim."
          : "Başvurunuzu hazırladım. Şimdi uygun gönderim kanalını açıyorum."}
      </p>
      <p className="mt-1 text-sm leading-relaxed text-slate-600">{agentMessage}</p>

      {isDigitalSubscription && billingProviderLabel && (
        <p data-testid="selected-billing-provider" className="mt-3 rounded-full bg-violet-50 px-3 py-2 text-xs font-semibold text-violet-800">
          Faturalandırma: {billingProviderLabel}
        </p>
      )}

      {institution?.notes && (
        <div data-testid="institution-guidance" className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm leading-relaxed text-slate-700">
          <span className="font-semibold text-[#0F3D4A]">Kurum bilgisi: </span>{institution.notes}
        </div>
      )}

      {discoveryLoading && (
        <div data-testid="channel-discovery-loading" role="status" className="mt-4 rounded-2xl border border-teal-100 bg-teal-50 p-4 text-sm font-semibold text-[#0F766E]">
          Kurumun resmî sitesinde güncel başvuru kanalını kontrol ediyorum.
        </div>
      )}

      {!isDigitalSubscription && (!isEcommerce || verifiedRecipient) && <div className="mt-5 rounded-2xl border border-teal-100 bg-teal-50 p-4" data-testid="delivery-preview">
        <dl className="grid gap-2 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-slate-500">Alıcı</dt>
            {verifiedRecipient && <span data-testid="verified-email-label" className="mt-1 inline-block text-xs font-semibold text-[#0F766E]">{resolution.primaryChannel.label}</span>}
            {liveRecipient && <span data-testid="live-email-label" className="mt-1 inline-block text-xs font-semibold text-amber-700">Resmî sitede bulunan e-posta kanalı</span>}
            <dd className="mt-0.5 flex items-center gap-2 font-semibold text-[#0F172A] break-all">
              {displayedRecipient}
              {recipient && <button type="button" onClick={() => copyValue(recipient, "recipient")} aria-label="Alıcı adresini kopyala" className="shrink-0 text-[#0F766E]"><Copy size={14} /></button>}
            </dd>
            {manualRecipientFormatValid && !trustedRecipient && <span className="mt-1 inline-block rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">Sizin girdiğiniz kurum adresi</span>}
          </div>
          <div>
            <dt className="text-slate-500">Gönderen</dt>
            <dd className="mt-0.5 font-semibold text-[#0F172A] break-all">{senderEmail || "E-posta uygulamanız"}</dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-slate-500">Konu</dt>
            <dd data-testid="email-subject" className="mt-0.5 font-semibold text-[#0F172A] break-words">{emailSubject}</dd>
          </div>
          <div>
            <dt className="text-slate-500">Ekler</dt>
            <dd className="mt-0.5 font-semibold text-[#0F172A]">{attachmentCount ? `${attachmentCount} belge` : "Belge yok"}</dd>
          </div>
        </dl>
      </div>}

      {channelRequirements.length > 0 && (
        <div data-testid="channel-requirements" className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <h3 className="font-semibold text-[#0F172A]">Göndermeden önce</h3>
          <ul className="mt-2 space-y-2 text-sm text-slate-700">
            {channelRequirements.map((requirement) => (
              <li key={requirement.code} className="flex items-start gap-2">
                <span aria-hidden="true" className="mt-0.5 font-bold text-amber-700">•</span>
                <span>{requirement.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {resolution.primaryChannel?.instructions && (
        <div data-testid="channel-instructions" className="mt-4 rounded-2xl border border-teal-100 bg-teal-50 p-4 text-sm leading-relaxed text-[#0F3D4A]">
          {resolution.primaryChannel.instructions}
        </div>
      )}

      {recipient && (
        <div className="mt-4" data-testid="email-action-group">
          {!primaryOfficialChannel && (
            <button
              type="button"
              data-testid="primary-delivery-action"
              onClick={() => openCompose(preferredProvider)}
              className="fs-btn-primary min-h-12 w-full inline-flex items-center justify-center gap-2 text-center"
            >
              <Mail size={17} /> {PROVIDER_LABELS[preferredProvider]}
            </button>
          )}
          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3" data-testid="send-actions">
            <button type="button" onClick={() => openCompose("gmail")} data-testid="open-gmail-btn" className="fs-btn-outline min-h-11 w-full text-sm">Gmail’de aç</button>
            <button type="button" onClick={() => openCompose("outlook")} data-testid="open-outlook-btn" className="fs-btn-outline min-h-11 w-full text-sm">Outlook’ta aç</button>
            <button type="button" onClick={() => openCompose("mailto")} data-testid="open-mailto-btn" className="fs-btn-outline min-h-11 w-full text-sm">Diğer e-posta uygulaması</button>
          </div>
        </div>
      )}

      {registryAlternativeChannels.length > 0 && (
        <div data-testid="alternative-official-channels" className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <h3 className="text-sm font-semibold text-[#0F172A]">Alternatif resmî kanal</h3>
          <div className="mt-3 grid grid-cols-1 gap-2">
            {registryAlternativeChannels.map((channel) => (
              <button key={channel.id} type="button" data-testid={`alternative-channel-${channel.id}`} onClick={() => copyThenOpenOfficialChannel(channel)} className="fs-btn-outline min-h-11 w-full inline-flex items-center justify-center gap-2 text-sm">
                <ExternalLink size={15} /> {channel.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {supportingInfoChannels.length > 0 && (
        <div data-testid="supporting-official-channels" className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <h3 className="text-sm font-semibold text-[#0F172A]">Diğer resmî iletişim yolları</h3>
          <dl className="mt-2 space-y-2 text-sm text-slate-700">
            {supportingInfoChannels.map((channel) => (
              <div key={channel.id} className="flex flex-wrap items-baseline justify-between gap-2">
                <dt>{channel.label}</dt>
                <dd className="font-semibold text-[#0F3D4A]">{channel.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      {visibleEscalationChannels.length > 0 && (
        <div data-testid="escalation-official-channels" className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-[#0F172A]">Alternatif e-Devlet yolu</h3>
          <p className="mt-1 text-xs leading-relaxed text-slate-600">Abonelik İptal e-Devlet şifrenizi istemez ve işlemi sizin adınıza yapmaz. Başvuruyu açılan resmî sayfada siz tamamlarsınız.</p>
          <div className="mt-3 grid grid-cols-1 gap-2">
            {visibleEscalationChannels.map((channel) => (
              <a key={channel.id} href={channel.value} target="_blank" rel="noopener noreferrer" data-testid={`escalation-channel-${channel.id}`} className="fs-btn-outline min-h-11 w-full inline-flex items-center justify-center gap-2 text-center text-sm">
                <ExternalLink size={15} /> {channel.label}
              </a>
            ))}
          </div>
        </div>
      )}

      {primaryOfficialChannel && (
        <div className="mt-4">
          {!isLivePrimary && primaryOfficialChannel.type === CHANNEL_TYPES.OFFICIAL_FORM && (
            <p data-testid="verified-official-form-label" className="mb-2 text-xs font-semibold text-[#0F766E]">Doğrulanmış resmî başvuru sayfası</p>
          )}
          {!isLivePrimary && primaryOfficialChannel.type === CHANNEL_TYPES.ONLINE_ACCOUNT && (
            <p data-testid="verified-online-account-label" className="mb-2 text-xs font-semibold text-[#0F766E]">Doğrulanmış online işlemler kanalı</p>
          )}
          {!isLivePrimary && primaryOfficialChannel.type === CHANNEL_TYPES.OFFICIAL_CONTACT_PAGE && (
            <p data-testid="verified-contact-page-label" className="mb-2 text-xs font-semibold text-[#0F766E]">Doğrulanmış resmî iletişim sayfası</p>
          )}
          {isLivePrimary && (
            <p data-testid="live-channel-label" className="mb-2 text-xs font-semibold text-amber-700">Resmî sitede canlı olarak bulunan kanal</p>
          )}
          <button
            type="button"
            data-testid="primary-delivery-action"
            onClick={() => copyThenOpenOfficialChannel(primaryOfficialChannel)}
            className="fs-btn-primary min-h-12 w-full inline-flex items-center justify-center gap-2 text-center"
          >
            <ExternalLink size={17} /> {primaryOfficialChannel.label}
          </button>
        </div>
      )}

      {!isDigitalSubscription && !isEcommerce && !verifiedRecipient && !liveRecipient && (
        <div className="mt-5" data-testid="unverified-email-option">
          {!primaryOfficialChannel && !trustedRecipient && !discoveryLoading && (
            <div className="mb-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <h3 className="font-semibold text-[#0F172A]">Bu kurum için doğrulanmış doğrudan başvuru kanalı henüz bulunmuyor.</h3>
              <p className="mt-1 text-sm text-slate-600">Doğrulanmamış bir adres önermiyorum. Kurumun e-posta adresini biliyorsanız girebilir veya mevcut resmî iletişim seçeneklerini kullanabilirsiniz.</p>
            </div>
          )}
          <button type="button" data-testid="manual-email-toggle" onClick={() => setManualEmailOpen((open) => !open)} aria-expanded={manualEmailOpen} className="text-sm font-semibold text-[#0F766E] underline underline-offset-4">Kurumun e-posta adresini biliyorum</button>
          {manualEmailOpen && (
            <div className="mt-3">
              <label htmlFor="manualInstitutionEmail" className="block text-sm font-semibold text-[#0F3D4A]">Kurumun alıcı e-posta adresi</label>
              <input
                id="manualInstitutionEmail"
                data-testid="manual-recipient-input"
                type="email"
                inputMode="email"
                value={manualRecipient}
                onChange={(event) => setManualRecipient(event.target.value)}
                aria-invalid={manualRecipientHasValue && !manualRecipientValid}
                aria-describedby="manualInstitutionEmailHelp manualInstitutionEmailError"
                className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:outline-none focus:border-[#4DA6A1]"
              />
              {manualRecipientMatchesSender && <p id="manualInstitutionEmailError" role="alert" className="mt-1.5 text-sm text-red-600">Alıcı adresi kendi e-posta adresinizle aynı olamaz. Başvuruyu göndereceğiniz kurumun e-posta adresini yazın.</p>}
              {manualRecipientHasValue && !manualRecipientFormatValid && <p id="manualInstitutionEmailError" role="alert" className="mt-1.5 text-sm text-red-600">Geçerli bir e-posta adresi girin.</p>}
              <p id="manualInstitutionEmailHelp" className="mt-1.5 text-xs text-slate-500">Kendi e-posta adresinizi değil, başvuruyu göndereceğiniz kurumun adresini yazın. Bu adres doğrulanmış kurum kanalına kaydedilmez.</p>
            </div>
          )}
        </div>
      )}

      {fallbackChannel && (
        <div data-testid="live-candidate-fallback" className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm text-amber-900">Resmî sitede olası bir iletişim kanalı bulundu ancak doğrudan fesih kanalı olduğu doğrulanamadı.</p>
          <a href={fallbackChannel.value} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-[#0F766E] underline underline-offset-2">
            Resmî iletişim sayfasını görüntüle <ExternalLink size={14} />
          </a>
        </div>
      )}

      {resolution.reviewRequired && (
        <div data-testid="channel-review-notice" className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          {resolution.verificationStatus === CHANNEL_STATUSES.STALE
            ? "Bu kanal daha önce doğrulanmış ancak güncelliğinin yeniden kontrol edilmesi gerekiyor."
            : "Bu kanal henüz inceleme aşamasında olduğu için gönderim aksiyonu olarak kullanılamaz."}
        </div>
      )}

      {isDigitalSubscription && !primaryOfficialChannel && resolution.reviewChannels
        .filter((channel) => channel.type === CHANNEL_TYPES.OFFICIAL_CONTACT_PAGE && channel.value)
        .map((channel) => (
          <a
            key={channel.id}
            href={channel.value}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`digital-review-fallback-${channel.id}`}
            className="fs-btn-outline mt-4 min-h-11 w-full inline-flex items-center justify-center gap-2 text-center text-sm"
          >
            <ExternalLink size={15} /> {channel.label} · yalnız bilgi için görüntüle
          </a>
        ))}

      {source && (
        <details data-testid="channel-source-details" className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm">
          <summary className="cursor-pointer font-semibold text-[#0F3D4A]">Kaynak ve doğrulama bilgisi</summary>
          <dl className="mt-3 grid gap-2 sm:grid-cols-2">
            <div>
              <dt className="text-xs text-slate-500">Kaynak</dt>
              <dd className="mt-0.5 font-semibold text-slate-800">
                <a href={source.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#0F766E] underline underline-offset-2">
                  {source.title} <ExternalLink size={13} />
                </a>
              </dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500">Resmî domain</dt>
              <dd className="mt-0.5 font-semibold text-slate-800">{source.officialDomain}</dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500">{isLivePrimary ? "Bulunma zamanı" : "Son kontrol"}</dt>
              <dd className="mt-0.5 font-semibold text-slate-800">{source.verifiedAt}</dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500">Güven durumu</dt>
              <dd className="mt-0.5 font-semibold text-slate-800">
                {isLivePrimary
                  ? "Yüksek · Canlı güçlü aday"
                  : source.confidence === CHANNEL_CONFIDENCE.HIGH ? "Yüksek · Doğrulanmış" : source.confidence}
              </dd>
            </div>
          </dl>
        </details>
      )}

      {!isDigitalSubscription && attachmentCount > 0 && (
        <div data-testid="attachment-reminder" className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-start gap-3">
            <Paperclip size={16} className="mt-0.5 shrink-0 text-amber-700" />
            <p className="text-sm font-semibold text-slate-700">{attachmentCount} belgeniz hazır. E-posta ekranında dosyaları eklemeyi unutmayın.</p>
          </div>
          {attachments.length > 0 && (
            <details className="mt-2 text-xs text-slate-600">
              <summary className="cursor-pointer font-semibold">Belge adlarını görüntüle</summary>
              <ul className="mt-2 space-y-1">{attachments.map((file, index) => <li key={`${file.name}-${index}`}>{file.name}</li>)}</ul>
            </details>
          )}
        </div>
      )}

      {!isDigitalSubscription && <details data-testid="secondary-copy-options" className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
        <summary className="cursor-pointer font-semibold text-[#0F3D4A]">Metni görüntüle ve düzenle · Diğer seçenekler</summary>
        <textarea
          value={generatedText}
          onChange={(event) => onGeneratedTextChange?.(event.target.value)}
          readOnly={!onGeneratedTextChange}
          rows={9}
          data-testid="editable-application-text"
          className="mt-4 w-full rounded-2xl border border-slate-200 bg-white p-4 text-sm leading-relaxed text-slate-800 focus:outline-none focus:border-[#4DA6A1]"
        />
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
          <button type="button" onClick={() => copyValue(emailSubject, "subject")} data-testid="copy-subject-btn" className="fs-btn-outline min-h-10 text-sm"><Copy size={14} className="mr-1 inline" /> Konuyu kopyala</button>
          <button type="button" onClick={() => copyValue(generatedText, "text")} data-testid="send-copy-text-btn" className="fs-btn-outline min-h-10 text-sm"><Copy size={14} className="mr-1 inline" /> Metni kopyala</button>
          <button type="button" onClick={() => copyValue(`${emailSubject}\n\n${generatedText}`, "all")} data-testid="copy-all-btn" className="fs-btn-outline min-h-10 text-sm"><Copy size={14} className="mr-1 inline" /> Tüm başvuruyu kopyala</button>
        </div>
      </details>}

      <div className="mt-4 flex items-start gap-3 text-xs leading-relaxed text-slate-500">
        <ShieldAlert size={15} className="mt-0.5 shrink-0 text-[#E76A5A]" />
        <p>Abonelik İptal başvuruyu sizin adınıza göndermez ve platform şifrenizi istemez. Açılan e-posta veya resmî sayfadaki bilgileri kontrol edip işlemi siz tamamlarsınız.</p>
      </div>
      {!resolution.institution && <div className="sr-only"><Info /> Kurum kanal kaydı bulunamadı.</div>}
    </section>
  );
}
